/* eslint-disable @typescript-eslint/no-explicit-any */
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import Loading from '@components/Loading';
import { AnimationType } from '@components/types';
import { withHeaderFooter } from '@components_v2/HOCs';
import { ExitIntent } from '@components_v2/UI/atoms/exit-intent/ExitIntent';
import { DigiAppNavigationProps } from '@components_v2/data';
import { FullAppPage, i18n } from '@fmc-digital/marketing';
import { useAbandonedScreenMutation } from '@hooks/useAbandonedScreenMutation';
import { ReadyStateScreen, useReadyStateQuery } from '@hooks/useReadyStatusQuery';
import { Container, useTheme } from '@mui/material';
import { RootState } from '@store';
import { removeTileOffer, setSelectedLoanNumber } from '@store/actions/widgets.actions';
import { selectFeatureFlag } from '@store/reducers/config.reducer';
import { selectSelectedLoanNumber } from '@store/selectors/widgets.selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Borrower, NonBorrowingSpouse } from '@types';
import { camelCase } from '@utils/addressFormat';
import { AxiosError } from 'axios';
import { activityEventUpdate, ApiLogger } from 'fmc-digital-common';
import i18next from 'i18next';
import { get, isEmpty } from 'lodash';
import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useExitIntent } from 'use-exit-intent';
import { v4 } from 'uuid';
import { auth, config } from '../config';
import { useLoanSummaryQuery } from './fullAppV2/hooks/useLoanSummaryQuery';
import { sanitizeNBSObj, useFullAppPrefill } from './fullAppV2/hooks/usePrefillQuery';
import { useWidgetsQueryClient } from '@hooks/useWidgetsQueryClient';
import { fullAppFlows } from '@constants';
const FullAppContainer = forwardRef((_, ref) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { updateCache } = useWidgetsQueryClient();
    const params = useParams();
    const { state } = useLocation();
    const [open, setOpen] = useState(false);
    const [fullAppFlow, setFullAppFlow] = useState(false);
    const [appId, setAppId] = useState('');
    const [loanNumber, setLoanNumber] = useState('');
    const [leadError, setLeadError] = useState(false);
    const [pollingLoanSummary, setPollingLoanSummary] = useState<boolean>(false);
    const [pollingLoanInProgress, setPollingLoanInProgress] = useState<boolean>(false);
    const [timeOutError, setTimeOutError] = useState(false);
    const [getLoanSummary, setGetLoanSummary] = useState<boolean>(false);
    const [waitingLobbyAnimation, setWaitingLobbyAnimation] = useState<string | undefined>();
    const [bailOutScreen, setBailOutScreen] = useState('initial_offer');
    const ref2 = useRef(null);
    const isLeadInitiated = useRef(false);
    const selectedLoanData = useSelector(selectSelectedLoanNumber);
    const { registerHandler, unsubscribe, resetState } = useExitIntent({
        desktop: { triggerOnIdle: false, triggerOnMouseLeave: false, useBeforeUnload: false, delayInSecondsToTrigger: 2 },
        mobile: { triggerOnIdle: false, delayInSecondsToTrigger: 2 }
    });
    const isExitIntentEnabled = useSelector((state: RootState) => selectFeatureFlag(state, 'enableExitIntent'));
    registerHandler({
        id: 'registerHandler',
        handler: () => setOpen(isExitIntentEnabled)
    });
    const {
        data: prefillData,
        status: prefillStatus,
        isError: prefillError
    } = useQuery(useFullAppPrefill({ loanNumber: params.loanNumber }));
    const summaryQuery = useLoanSummaryQuery(getLoanSummary, appId);
    const { data: loanSummaryData, isError: isLoanSummaryError } = summaryQuery;
    const readyStateLoanInProgress = useReadyStateQuery({
        enabled: pollingLoanInProgress,
        screen: ReadyStateScreen.LOAN_IN_PROGRESS,
        appId
    });
    const { status: loanInProgressStatus, error: lipError } = readyStateLoanInProgress;
    const readyStateLoanSummary = useReadyStateQuery({
        enabled: pollingLoanSummary,
        screen: ReadyStateScreen.LOAN_SUMMARY,
        appId
    });
    const { status: loanSummaryStatus, isError: loanSummaryStatusError } = readyStateLoanSummary;
    const offerTerms: string = i18next.t('offerTerms', { returnObjects: false, ns: 'translation' });
    const abandonedScreen = useAbandonedScreenMutation(appId);
    const { offer: offerData } = state || { offer: undefined };
    React.useEffect(() => {
        if (loanSummaryStatus === 'success') {
            setWaitingLobbyAnimation(undefined);
            setGetLoanSummary(true);
        }
    }, [loanSummaryStatus, loanSummaryStatusError]);
    const firstName = useMemo(() => {
        if (!isEmpty(prefillData)) {
            const name = get(prefillData, 'borrowers', []).find((ele: { type: string }) => ele.type.toLowerCase() === 'primary').firstName;
            return camelCase(name || '');
        }
        return '';
    }, [prefillData]);
    React.useImperativeHandle(ref, () => {
        return {
            triggerEvent: async (name: string) => {
                if (isEmpty(name)) {
                    setOpen(true);
                } else if (name === 'Log Out') {
                    await handleAbandonedScreenApi(false);
                } else {
                    setOpen(false);
                }
            }
        };
    });
    const handleOnLoanSummaryTrigger = useCallback(() => {
        setWaitingLobbyAnimation(AnimationType.LOAN_SUMMARY);
        if (!pollingLoanInProgress) {
            setPollingLoanSummary(true);
        }
        setBailOutScreen('loan_summary');
    }, [pollingLoanInProgress]);
    const postLeadApi = async (data: any) => {
        ApiLogger(
            {
                method: 'post',
                url: config.env('prefillUrl'),
                data,
                headers: {
                    'x-correlation-id': v4().toString(),
                    Authorization: `Bearer ${auth.getOkta().getAccessToken()}`,
                    'x-api-key': config.env('xApiKey'),
                    source: 'DigiApp'
                }
            },
            logApiEvent
        )
            .then(res => {
                setAppId(get(res, ['applicationId'], ''));
            })
            .catch(err => {
                setLeadError(true);
                console.log(err);
            });
    };
    const sanitizeDemographics = (data: any) => {
        if (!isEmpty(data)) return data;
        const { borrowers } = data;
        const nullifiedDemographics = borrowers.map((borrower: any) => Object.assign(borrower, { demographic: null }));
        return Object.assign(data, { borrowers: nullifiedDemographics });
    };
    const sanitizePersonalInformation = (data: any) => {
        if (isEmpty(data)) return data;
        const { borrowers } = data;
        const nullifiedDeclarations = borrowers.map((borrower: any) =>
            Object.assign(borrower, {
                declarations: null,
                domesticRelationshipIndicator: false,
                nonBorrowingSpouses: borrower.nonBorrowingSpouses
                    ? borrower.nonBorrowingSpouses?.map((nbs: NonBorrowingSpouse) => sanitizeNBSObj(nbs))
                    : null
            })
        );
        return Object.assign(data, { borrowers: nullifiedDeclarations });
    };
    const sanitizeLeadRequestBody = (data: any, tracked: any) => {
        if (isEmpty(tracked)) return data;
        return Object.values(tracked).reduce(
            //@ts-ignore
            (acc: any, curr: string, idx: number) => {
                if (['COMPLETED', 'NOT_STARTED'].includes(curr) || isEmpty(data)) return acc;
                if (idx === 2) return sanitizeDemographics(acc);
                if (idx === 3) return sanitizePersonalInformation(acc);
                return acc;
            },
            data
        );
    };
    const handleOnSubmit = useCallback(async (loanNumber: string) => {
        setWaitingLobbyAnimation(AnimationType.DISCLOSURE);
        setLoanNumber(loanNumber);
        setTimeout(() => {
            setPollingLoanInProgress(true);
        }, 5000);
    }, []);
    const handleLeadApi = async (data: any, screen: string, tracked?: any, _?: boolean) => {
        let sanitized = data;
        if (screen === 'loan_summary' || screen === 'declarations') {
            sanitized = {
                ...sanitized,
                borrowers: sanitized.borrowers.map((borrower: Borrower) => ({
                    ...borrower,
                    nonBorrowingSpouses: borrower.nonBorrowingSpouses
                        ? borrower.nonBorrowingSpouses.map((nbs: NonBorrowingSpouse) => sanitizeNBSObj(nbs))
                        : null
                }))
            };
        } else sanitized = sanitizeLeadRequestBody(sanitized, tracked);
        const mutatedPut = {
            ...sanitized,
            screen,
            product: offerData?.product_Campaign,
            offerGUID: offerData?.offerGUID,
            referralSource: 'ACP',
            utmDetails: {
                utmDevice: 'Web',
                utmDevicemodel: 'Chrome'
            }
        };
        if (appId)
            ApiLogger(
                {
                    method: 'put',
                    url: `${config.env('prefillUrl')}/${appId}`,
                    data: mutatedPut,
                    headers: {
                        'x-correlation-id': v4().toString(),
                        Authorization: `Bearer ${auth.getOkta().getAccessToken()}`,
                        'x-api-key': config.env('xApiKey'),
                        source: 'DigiApp'
                    }
                },
                logApiEvent
            ).catch(err => {
                setLeadError(true);
                console.log(err);
            });
        else await postLeadApi(mutatedPut);
        if (screen === 'loan_summary') {
            const loanNumber = get(loanSummaryData, ['headerDetails', 'loanNumber'], undefined);
            logFullAppUserEvent(!loanNumber ? 'FinalOfferSubmitApplicationFailure' : 'FinalOfferSubmitApplicationSuccess');
            if (loanNumber) {
                await queryClient.invalidateQueries({ queryKey: ['widgets'] });
                dispatch(setSelectedLoanNumber({ identifier: loanNumber, type: 'OriginationLoanNumber' }));
                await handleOnSubmit(loanNumber);
            }
        }
    };
    const onSubmitNotNowReason = async (data: any, reason: any, screen: string, isCallUsModal: boolean) => {
        const sanitized = {
            ...data,
            borrowers: data.borrowers.map((borrower: Borrower) => ({
                ...borrower,
                nonBorrowingSpouses: borrower.nonBorrowingSpouses
                    ? borrower.nonBorrowingSpouses?.map((nbs: NonBorrowingSpouse) => sanitizeNBSObj(nbs))
                    : null
            }))
        };
        const abandonedReasonBody = Object.assign(
            isCallUsModal === true
                ? {
                      callUsModal: true
                  }
                : {
                      surveyQuestions: reason
                  }
        );
        const mutated = {
            ...sanitized,
            screen,
            product: offerData?.product_Campaign,
            offerGUID: offerData?.offerGUID,
            referralSource: 'ACP',
            utmDetails: {
                utmDevice: 'Web',
                utmDevicemodel: 'Chrome'
            },
            ...abandonedReasonBody
        };
        if (appId)
            ApiLogger(
                {
                    method: 'put',
                    url: `${config.env('prefillUrl')}/${appId}`,
                    data: mutated,
                    headers: {
                        'x-correlation-id': v4().toString(),
                        Authorization: `Bearer ${auth.getOkta().getAccessToken()}`,
                        'x-api-key': config.env('xApiKey'),
                        source: 'DigiApp'
                    }
                },
                logApiEvent
            ).catch(err => {
                setLeadError(true);
                console.log(err);
            });
        else await postLeadApi(mutated);
        navigate('/', { preventScrollReset: true });
    };
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    const handleAbandonedScreenApi = async (haveQuestions: boolean = true) => {
        // eslint-disable-next-line @typescript-eslint/ban-types
        let bailout;
        if (typeof bailOutScreen === 'string') {
            bailout = { abandonedScreen: bailOutScreen, haveQuestions: haveQuestions };
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-types
            bailout = { ...(bailOutScreen as Object), haveQuestions: haveQuestions };
        }
        if (appId) abandonedScreen.mutate(bailout);
    };
    const returnToDashboard = (arg?: string) => {
        if (arg === 'initial_offer') setFullAppFlow(false);
        else if (arg === 'kick_out') navigate('/');
        else setOpen(true);
    };
    const handleBailOutScreen = useCallback((screen: string) => {
        setBailOutScreen(screen);
    }, []);
    console.log('BailOut ', bailOutScreen);
    const logFullAppUserEvent = (action: string) => {
        logUserEvent({
            type: 'DigiApp',
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            loanType: offerData?.portCode,
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            productCode: selectedLoanData?.loanType,
            loanNumber: params.loanNumber,
            action: action
        });
    };
    React.useEffect(() => {
        queryClient.resetQueries(['ready-state']).catch(console.log);
        queryClient.resetQueries(['loanSummary']).catch(console.log);
        setGetLoanSummary(false);
        resetState();
    }, []);
    React.useEffect(() => {
        if (loanInProgressStatus === 'success') {
            navigate(`/new-loan/${loanNumber}`, {
                state: ['status_tracker_widget', 'doc_upload_widget', 'disclosure_widget', 'loan-in-progress-task']
            });
        }
        if ((lipError as AxiosError)?.response.status === 202) {
            setPollingLoanInProgress(false);
            setTimeOutError(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanInProgressStatus, lipError]);
    React.useEffect(() => {
        if (prefillStatus === 'success' && !isEmpty(prefillData)) {
            const { length } = get(prefillData, ['borrowers'], { length: 0 });
            if (length > 2) {
                navigate(`../${offerData?.product_Campaign}/${params.loanNumber}`, { state: get(state, ['offer'], undefined) });
                return;
            } else if (!isLeadInitiated.current) {
                handleLeadApi(prefillData, 'initial_offer').catch(console.log);
                isLeadInitiated.current = true;
            }
        }
    }, [prefillData, prefillStatus]);
    React.useEffect(() => {
        const loanNumber = get(loanSummaryData, ['headerDetails', 'loanNumber'], undefined);
        if (loanNumber) {
            const url = config.env('activityUpdateUrl');
            activityEventUpdate(
                [
                    {
                        loanNumber: params.loanNumber,
                        activityType: 'SL',
                        activityResult: 'SUBMITTED'
                    }
                ],
                url
            );
            const offerId = { loanNumber: params.loanNumber, product: 'SL', offerGUID: offerData?.offerGUID };
            updateCache(offerId);
            dispatch(removeTileOffer(offerId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanSummaryData]);
    if (prefillStatus === 'loading') return <Loading />;
    const error =
        prefillError ||
        leadError ||
        isLoanSummaryError ||
        loanSummaryStatusError ||
        (lipError && (lipError as AxiosError))?.response?.status >= 400;
    if (error) {
        navigate('/error', {
            state: {
                text: leadError && 'We apologize for the inconvenience. Your information has been saved to this point in your application.',
                subText: leadError && 'We will contact you shortly to assist with your application process.',
                appId: appId,
                screen: bailOutScreen,
                exitIntent: true
            },
            preventScrollReset: false
        });
    }
    if (timeOutError) {
        navigate('/error', {
            state: {
                text: `We apologize for the inconvenience.\nYou will get an email notification when your disclosures have been generated.`,
                title: '',
                subtitle: 'Your disclosures are taking longer than expected.',
                appId: appId,
                screen: bailOutScreen,
                exitIntent: true
            },
            preventScrollReset: false
        });
    }
    const flowStructure = fullAppFlows.FHA;
    return (
        <I18nextProvider i18n={i18n}>
            <Container maxWidth="md" sx={{ paddingTop: theme.spacing(4) }}>
                <ExitIntent
                    open={open}
                    onContinue={() => {
                        resetState();
                        setOpen(false);
                    }}
                    onBackToDashboard={async () => {
                        handleAbandonedScreenApi(false);
                        unsubscribe();
                        navigate('/', { preventScrollReset: true });
                    }}
                />
                {isEmpty(prefillData) ? null : (
                    <FullAppPage
                        data={prefillData}
                        handleLeadApi={handleLeadApi}
                        appId={appId}
                        returnToDashboard={(arg?: string) => returnToDashboard(arg)}
                        firstName={firstName}
                        onSubmitNotNowReason={onSubmitNotNowReason}
                        logUserEvent={logFullAppUserEvent}
                        ref={ref2}
                        offerData={offerData}
                        //@ts-ignore
                        offerTerms={offerTerms}
                        summaryQuery={summaryQuery}
                        onTriggerLoanSummary={handleOnLoanSummaryTrigger}
                        setBailOutScreenName={handleBailOutScreen}
                        fullAppFlow={fullAppFlow}
                        setFullAppFlow={setFullAppFlow}
                        //@ts-ignore
                        waitingLobbyAnimation={waitingLobbyAnimation}
                        //@ts-ignore
                        handleAbandonedLead={handleAbandonedScreenApi}
                        flowData={flowStructure}
                    />
                )}
            </Container>
        </I18nextProvider>
    );
});
FullAppContainer.displayName = 'FullAppContainer';
export default withHeaderFooter(FullAppContainer, { ...DigiAppNavigationProps });
